if (!customElements.get('product-form')) {
  const describeLine = (line) =>
    `${line.variant_id} x${line.quantity} ${String(line.key).split(':').pop().slice(0, 8)} ${Object.keys(line.properties || {}).join('|')}`;

  // Cart writes by any script from 3s before this add on, logged by snippets/prime-cart.liquid.
  const recentCartWrites = (since) =>
    (window.__loCartWrites || [])
      .filter((write) => write.t >= since - 3000)
      .map((write) => `${Math.round(write.t - since)}ms ${write.url} ${write.shape} @ ${write.src}`);

  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      this.form.querySelector('[name=id]').disabled = false;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.cartNotification = document.querySelector('cart-notification');

      // TIERED DISCOUNTS - splitting vars into separate line items:
      // check current variant against cart line items - if it exists, 
      // then add a hidden line item prop to force this variant to be added
      // as a separate line item
      this.checkForDuplicateVariants(this.querySelector('[name=id').value);
    }

    onSubmitHandler(evt) {
      evt.preventDefault();
      const submitButton = this.querySelector('[type="submit"]');
      if (submitButton.classList.contains('loading')) return;

      this.handleErrorMessage();

      submitButton.setAttribute('aria-disabled', true);
      submitButton.classList.add('loading');
      this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(this.form);

      // This random value is there so if the same product is added twice it would show up as 2 separate products
      const randomVariantIndex = Math.floor(1000 + Math.random() * 9000);

      // Append or override the _variantIndex with the random value
      formData.set('properties[_variantIndex]', randomVariantIndex);
      
      formData.append('sections', this.cartNotification.getSectionsToRender().map((section) => section.id));
      formData.append('sections_url', window.location.pathname);
      config.body = formData;

      const addStartedAt = performance.now();
      const quantity = Number(formData.get('quantity')) || 1;
      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.handleErrorMessage(response.description);
            return;
          }

          this.cartNotification.renderContents(response);
          this.verifyAdded(response, addStartedAt, quantity);
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          submitButton.classList.remove('loading');
          submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading-overlay__spinner').classList.add('hidden');
          this.checkForDuplicateVariants(this.querySelector('[name=id').value);
        });
    }

    // A successful add can still leave the cart without the line. Two ways are
    // known: a cookie-less write by another script mints a second cart and wins
    // the cookie (the cart reads back empty), and another script's cart write
    // from a stale copy of the cart drops the new line from a non-empty cart.
    // Either way the line is re-added once, as the exact line that was added,
    // and only after the cart has read back without it: once for an empty cart,
    // twice (700ms and 2.7s) for a non-empty one, so an add that did land is
    // never doubled. A line that turns up late, or re-keyed with the same
    // _variantIndex, is only reported. If the re-add is lost too, give up:
    // redraw the drawer from the real cart, close it and show an error.
    verifyAdded(added, addStartedAt, quantity) {
      const retryReason = this.retryReason;
      this.retryReason = null;

      setTimeout(() => {
        this.readCart()
          .then((cart) => {
            const details = this.cartDetails(added, cart, addStartedAt);

            if (this.hasLine(cart, added)) {
              if (retryReason) this.reportRecovered(retryReason, details, added);
              return;
            }

            if (retryReason) {
              if (this.hasRekeyedLine(cart, added)) {
                this.reportCartMismatch('line rekeyed', { ...details, after_retry: retryReason });
                return;
              }
              this.reportCartMismatch(`${retryReason} retry failed`, details);
              this.giveUpOnAdd();
              return;
            }

            if (cart.item_count === 0) {
              this.reportCartMismatch('orphaned', details);
              this.retryAdd(added, 'orphan', quantity);
              return;
            }

            setTimeout(() => {
              this.readCart()
                .then((later) => {
                  details.cart_lines_after_2s = later.items.map(describeLine);
                  details.cart_writes = recentCartWrites(addStartedAt);

                  if (this.hasLine(later, added)) {
                    this.reportCartMismatch('line late', details);
                  } else if (this.hasRekeyedLine(later, added)) {
                    this.reportCartMismatch('line rekeyed', details);
                  } else {
                    this.reportCartMismatch('line missing', details);
                    this.retryAdd(added, 'line missing', quantity);
                  }
                })
                .catch((e) => console.error(e));
            }, 2000);
          })
          .catch((e) => console.error(e));
      }, 700);
    }

    readCart() {
      return fetch('/cart.js', { cache: 'no-store' }).then((response) => response.json());
    }

    hasLine(cart, added) {
      return cart.items.some((item) => item.key === added.key);
    }

    // A line re-keyed by another script keeps this add's random _variantIndex.
    sameIndexLines(cart, added) {
      const index = added.properties && added.properties._variantIndex;
      if (!index) return 0;
      return cart.items.filter(
        (item) => item.variant_id === added.variant_id && item.properties && item.properties._variantIndex === index
      ).length;
    }

    hasRekeyedLine(cart, added) {
      return this.sameIndexLines(cart, added) > 0;
    }

    cartDetails(added, cart, addStartedAt) {
      return {
        variant_id: added.variant_id,
        cart_item_count: cart.item_count,
        seconds_since_load: Math.round(performance.now() / 1000),
        added_line: describeLine(added),
        cart_lines: cart.items.map(describeLine),
        cart_writes: recentCartWrites(addStartedAt),
      };
    }

    // The re-added line carries _lo_readded (hidden in the drawer and checkout,
    // shown on the order in admin) so recovered adds can be matched to orders
    // without another cart write.
    retryAdd(added, reason, quantity) {
      const addStartedAt = performance.now();
      const fail = (error) => {
        this.reportCartMismatch(`${reason} retry failed`, { variant_id: added.variant_id, retry_error: String(error) });
        this.giveUpOnAdd(error && error.shopperMessage);
      };
      fetch(`${routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({
          items: [{ id: added.variant_id, quantity, properties: { ...(added.properties || {}), _lo_readded: reason } }],
          sections: this.cartNotification.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        }),
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status || !response.items || !response.items[0]) {
            fail({ toString: () => response.description || response.message || 'no line in response', shopperMessage: response.description });
            return;
          }

          this.retryReason = reason;
          this.verifyAdded(response.items[0], addStartedAt, quantity);
          this.cartNotification.renderContents(response);
        }, fail)
        .catch((e) => console.error(e));
    }

    // A slow re-key by another script can land after the re-add, leaving the
    // same pair twice; the later read reports that rather than guessing which
    // line to remove.
    reportRecovered(reason, details, added) {
      this.reportCartMismatch(`${reason} recovered`, details);

      setTimeout(() => {
        this.readCart()
          .then((cart) => {
            if (this.sameIndexLines(cart, added) > 1) {
              this.reportCartMismatch('line doubled', { ...details, cart_lines_after_5s: cart.items.map(describeLine) });
            }
          })
          .catch((e) => console.error(e));
      }, 5000);
    }

    giveUpOnAdd(shopperMessage) {
      const ids = this.cartNotification.getSectionsToRender().map((section) => section.id);
      fetch(`${window.location.pathname}?sections=${ids.join(',')}`)
        .then((response) => response.json())
        .then((sections) => this.cartNotification.renderContentsPassively({ sections }))
        .catch((e) => console.error(e));
      this.cartNotification.close();
      this.handleErrorMessage(shopperMessage || window.cartStrings.error);
    }

    reportCartMismatch(kind, details) {
      if (!window.Bugsnag || !Bugsnag.isStarted || !Bugsnag.isStarted()) return;

      Bugsnag.notify(new Error(`ATC ${kind}`), (event) => {
        event.groupingHash = `atc-${kind}`;
        event.severity = kind.endsWith('retry failed') || kind === 'line doubled' ? 'warning' : 'info';
        event.addMetadata('cart', details);
      });
    }

    handleErrorMessage(errorMessage = false) {
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }

    checkForDuplicateVariants(id) {
      // get cart items
      // check for current variant
      fetch('/cart.js')
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.handleErrorMessage(response.description);
            return;
          }
          const matchingVariants = response.items.filter(item => item.variant_id === parseInt(id));

          this.handleLineItemProp(matchingVariants.length);
          
        })
        .catch((e) => {
          console.error(e);
        })
    }

    handleLineItemProp(index) {
      const propInput = this.querySelector('[name="properties[_variantIndex]"]');
      if (!propInput) {
        return
      }

      propInput.value = index.toString();
    }
  });
}
